import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, Modal } from 'ui';
import CloseIcon from '@mui/icons-material/Close';
import Textarea from 'react-textarea-autosize';
import ColorConstants from 'constants/ColorConstants';
import LoadingButton from './LoadingButton';
import { VERSION_MISMATCH, AI_CODING_IN_PROGRESS } from 'constants/VersioningErrorConstants';

const LEFT_PADDING = 45;

const EditTranscriptModal = ({ show, text: propText, hideModal, onSave, saving, errorCode }) => {
  const [text, setText] = useState('');
  const [originalText, setOriginalText] = useState('');

  // Update text when prop changes (similar to getDerivedStateFromProps)
  useEffect(() => {
    if (propText !== originalText) {
      const newText = propText || '';
      setOriginalText(newText);
      setText(newText);
    }
  }, [propText, originalText]);

  const onTextChange = useCallback((event) => {
    setText(event.target.value);
  }, []);

  const handleSave = useCallback(() => {
    onSave(text);
  }, [onSave, text]);

  const refreshPage = () => {
    window.location.reload();
  };

  // Error configurations
  const ERROR_CONFIGS = {
    [VERSION_MISMATCH]: {
      message: "Someone else has edited this transcript. To avoid overwriting those changes,",
      linkText: "please refresh this page.",
      onClick: refreshPage,
    },
    [AI_CODING_IN_PROGRESS]: {
      message: "AI coding is currently in progress.",
      linkText: "Please wait until it completes before editing.",
      onClick: null,
    },
    default: {
      message: "Something went wrong.",
      linkText: "Please refresh this page and try again.",
      onClick: refreshPage,
    },
  };

  // Render error alert based on error code
  let errorComponent = null;
  if (errorCode) {
    const config = ERROR_CONFIGS[errorCode] || ERROR_CONFIGS.default;
    errorComponent = (
      <Alert variant="warning" style={{ textAlign: "center" }}>
        {config.message}
        {config.onClick ? (
          <Button variant="link" style={{ padding: 0 }} onClick={config.onClick}>
            {config.linkText}
          </Button>
        ) : (
          ` ${config.linkText}`
        )}
      </Alert>
    );
  }

  return (
    <div>
      <Modal show={show} onHide={hideModal}>
        <Modal.Body style={{ padding: 0, margin: 0 }}>
          <div style={{ padding: `15px ${LEFT_PADDING}px 10px ${LEFT_PADDING}px` }}>
            <div style={{ float: "right" }}>
              <CloseIcon
                data-testid="CloseIcon"
                onClick={hideModal}
                style={{ color: ColorConstants.LIGHT_GREY, cursor: "pointer", fontSize: 16 }}
              />
            </div>
            <div style={{ clear: "both" }} />
            <div>
              <h4 style={{ marginTop: 0, paddingTop: 0 }}>
                Edit Text
              </h4>
            </div>
            {errorComponent}
            <div style={{ marginTop: 10, width: "100%" }}>
              <Textarea 
                placeholder=""
                onChange={onTextChange}
                value={text}
                style={{  
                  width: "100%",
                  resize: 'none',
                  padding: '20px 30px',
                  borderColor: "#d7d7d7",
                  borderRadius: "5px",
                  outline: "none",
                }}
              />
            </div>
          </div>
          <div style={{ float: "right", marginTop: "0px", marginRight: "45px", marginBottom: "20px" }}>
            <LoadingButton 
              variant="blue" 
              loading={!!saving} 
              onClick={handleSave} 
              disabled={!!errorCode}
            >
              {!!errorCode ? "Cannot Save Changes" : "Save Changes"}
            </LoadingButton>
          </div>
          <div style={{ clear: "both" }} />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EditTranscriptModal;